import { z } from 'zod';

/* ─────────── Себестоимость и варианты исполнения раздела (раздел 6.6 ТЗ, поток 2) ─────────── */

const Money = z.number().finite().nullable();

export const VARIANT_KINDS = {
  own: 'Свои силы',
  subcontract: 'Субподряд по КП',
  mixed: 'Смешанный',
} as const;
export type VariantKind = keyof typeof VARIANT_KINDS;
export const VariantKindSchema = z.enum(Object.keys(VARIANT_KINDS) as [VariantKind, ...VariantKind[]]);

/** Виды затрат себестоимости и статьи финансового плана, в которые они попадают (БМ-03, лист «Inputs») */
export const COST_TYPES = {
  materials: { title: 'Материалы', article: 'Материалы' },
  equipment: { title: 'Оборудование', article: 'Оборудование' },
  delivery: { title: 'Доставка и ГСМ', article: 'Транспортные расходы (аренда авто, доставка)' },
  wages: { title: 'ФОТ рабочих', article: null },
  wageTaxes: { title: 'Начисления на ФОТ', article: 'Налоги и др. платежи в бюджет по проекту' },
  crewCosts: {
    title: 'Вахта: проживание, питание, дорога',
    article: 'Производственные расходы (Ж/д, авиа., корочки. хранение обор-я, охрана)',
  },
  perDiem: { title: 'Суточные', article: 'Командировочные расходы' },
  machines: { title: 'Механизмы', article: 'Транспортные расходы (аренда авто, доставка)' },
  subcontract: { title: 'Субподряд', article: 'СМР, ПНР' },
  overhead: { title: 'Накладные', article: 'Административные расходы по проекту' },
  contingency: { title: 'Непредвиденные', article: 'Непредвиденные расходы по проекту' },
} as const;
export type CostType = keyof typeof COST_TYPES;

/** Параметры расчёта варианта (БМ-04: трудоёмкость сметы, бригада, смена, ставки) */
export const VariantParams = z.object({
  /** Свои силы */
  crewSize: z.number().int().min(1).max(500).optional(),
  shiftHours: z.number().min(1).max(24).optional(),
  hourlyRate: z.number().min(0).optional(),
  taxesPct: z.number().min(0).max(100).optional(),
  lodgingPerDay: z.number().min(0).optional(),
  foodPerDay: z.number().min(0).optional(),
  perDiemPerDay: z.number().min(0).optional(),
  travelPerRotation: z.number().min(0).optional(),
  rotationDays: z.number().int().min(1).max(365).optional(),
  /** Расчётное число смен вместо нормативного (лист «ВНС-1-КЖ»: 69,7 по норме против 60 расчётных) */
  shiftsPlanned: z.number().min(0).optional(),
  /** Общие */
  deliveryPct: z.number().min(0).max(100).optional(),
  overheadPct: z.number().min(0).max(100).optional(),
  contingencyPct: z.number().min(0).max(100).optional(),
  /** Материалы: из сметы, по лучшим ценам КП и поставок компании (БМ-11) или своей суммой */
  materialsSource: z.enum(['estimate', 'offers', 'manual']).optional(),
  /** Материалы и оборудование: из сметы или своей суммой */
  materialsOverride: z.number().min(0).nullable().optional(),
  equipmentOverride: z.number().min(0).nullable().optional(),
  /** Субподряд: сумма по КП, с НДС или без, доля материалов в КП */
  subcontractAmount: z.number().min(0).optional(),
  subcontractVatIncluded: z.boolean().optional(),
  subcontractMaterialsIncluded: z.boolean().optional(),
  /** Смешанный: доля работ, отдаваемая субподряду (0–100 % трудоёмкости) */
  subcontractSharePct: z.number().min(0).max(100).optional(),
});
export type VariantParams = z.infer<typeof VariantParams>;

export const VariantInput = z.object({
  kind: VariantKindSchema,
  name: z.string().trim().min(2).max(200),
  params: VariantParams,
  offerId: z.uuid().nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type VariantInput = z.infer<typeof VariantInput>;
export const VariantPatch = VariantInput.partial();
export type VariantPatch = z.infer<typeof VariantPatch>;

/** Выбор варианта — с обоснованием (БМ-02) */
export const VariantSelectInput = z.object({ reason: z.string().trim().min(5).max(2000) });
export type VariantSelectInput = z.infer<typeof VariantSelectInput>;

/** Значения по умолчанию для расчёта своих сил (пакет «Бюджет_10.10.2026», лист «ВНС-1-КЖ») */
export const VARIANT_DEFAULTS = {
  crewSize: 16,
  shiftHours: 11,
  taxesPct: 21.5,
  rotationDays: 30,
  deliveryPct: 3,
  overheadPct: 10,
  contingencyPct: 3,
} as const;

/**
 * Ставки расчёта своих сил компании (решение 0.4 ТЗ v2.0): начисления на ФОТ, накладные и непредвиденные — в
 * настройках компании (`core.tenant_settings`, ключ `est.rates`), а не константой. Новый вариант исполнения берёт их,
 * если в нём ставка не задана; у сохранённого варианта ставки записаны в параметрах и от смены настройки не меняются.
 */
export const ESTIMATE_RATES_KEY = 'est.rates';
export const EstimateRates = z.object({
  taxesPct: z.number().min(0).max(100),
  overheadPct: z.number().min(0).max(100),
  contingencyPct: z.number().min(0).max(100),
});
export type EstimateRates = z.infer<typeof EstimateRates>;
export const ESTIMATE_RATES_DEFAULTS: EstimateRates = {
  taxesPct: VARIANT_DEFAULTS.taxesPct,
  overheadPct: VARIANT_DEFAULTS.overheadPct,
  contingencyPct: VARIANT_DEFAULTS.contingencyPct,
};
/** Ставки из настройки: неверное или пустое значение — ставка по умолчанию */
export function estimateRatesOf(raw: unknown): EstimateRates {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pick = (k: keyof EstimateRates) => {
    const v = r[k];
    return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100 ? v : ESTIMATE_RATES_DEFAULTS[k];
  };
  return {
    taxesPct: pick('taxesPct'),
    overheadPct: pick('overheadPct'),
    contingencyPct: pick('contingencyPct'),
  };
}
export const EstimateRatesView = EstimateRates.extend({
  /** Компания свои ставки не задавала — действуют значения по умолчанию */
  isDefault: z.boolean(),
  canEdit: z.boolean(),
});
export type EstimateRatesView = z.infer<typeof EstimateRatesView>;

export const CostVariant = z.object({
  id: z.uuid(),
  budgetLineId: z.uuid(),
  sectionLineId: z.uuid(),
  kind: VariantKindSchema,
  name: z.string(),
  params: VariantParams,
  estimateAmount: Money,
  laborHours: z.number(),
  /** Нормативное и расчётное число смен */
  shiftsNorm: z.number().nullable(),
  shiftsPlanned: z.number().nullable(),
  materials: Money,
  equipment: Money,
  delivery: Money,
  wages: Money,
  wageTaxes: Money,
  crewCosts: Money,
  perDiem: Money,
  machines: Money,
  subcontract: Money,
  overhead: Money,
  contingency: Money,
  total: Money,
  /** Плановая маржа варианта: смета − себестоимость */
  margin: Money,
  marginPct: z.number().nullable(),
  missing: z.array(z.string()),
  /** Сметы раздела изменились после расчёта: вариант нужно пересчитать (сумма сметы в расчёте и сейчас) */
  stale: z.boolean(),
  isSelected: z.boolean(),
  reason: z.string().nullable(),
  note: z.string().nullable(),
  offerId: z.uuid().nullable(),
  updatedAt: z.string(),
});
export type CostVariant = z.infer<typeof CostVariant>;

/** Себестоимость раздела: база из смет, варианты, выбранный */
export const SectionCost = z.object({
  sectionLineId: z.uuid(),
  sectionName: z.string(),
  /** Бюджет раздела с утверждёнными изменениями (БМ-10) */
  budgetAmount: Money,
  /** Утверждённые изменения раздела: допработы, экономия, перерасход */
  changesDelta: Money,
  /** Сметная стоимость привязанных смет и ресурсы из них */
  estimates: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), amount: Money })),
  estimateAmount: Money,
  base: z.object({
    laborHours: z.number(),
    machineHours: z.number(),
    materials: Money,
    equipment: Money,
    machines: Money,
    wages: Money,
  }),
  variants: z.array(CostVariant),
  /** Материалы смет раздела по плановым ценам проекта и лучшим ценам КП и поставок (БМ-11): сумма и доля найденных */
  offerMaterials: z.object({
    amount: Money,
    estimateAmount: Money,
    matched: z.number().int(),
    matchedShare: z.number(),
    rows: z.array(
      z.object({
        name: z.string(),
        unit: z.string(),
        quantity: z.number(),
        estimatePrice: Money,
        bestPrice: Money,
        /** plan — справочник плановых цен проекта, offer — КП, receipt — поступление на склад */
        source: z.enum(['plan', 'offer', 'receipt']).nullable(),
        supplier: z.string().nullable(),
        date: z.string().nullable(),
      }),
    ),
  }),
  /** Версия бюджета — черновик, и у человека право «Править бюджет» */
  canEdit: z.boolean(),
});
export type SectionCost = z.infer<typeof SectionCost>;

/** Сводка маржи проекта (БМ-08): сметная и плановая по выбранным вариантам */
export const ProjectMargin = z.object({
  estimateAmount: Money,
  /** Утверждённые изменения бюджета (БМ-10) — уже внутри бюджета и плановой себестоимости */
  budgetChangesTotal: Money,
  /** Себестоимость по бюджету без вариантов (сметная маржа) и плановая по выбранным вариантам */
  budgetCost: Money,
  plannedCost: Money,
  estimateMargin: Money,
  estimateMarginPct: z.number().nullable(),
  plannedMargin: Money,
  plannedMarginPct: z.number().nullable(),
  /** Затраты площадки и АУП версии бюджета (БМ-07), входят в плановую себестоимость */
  siteCosts: Money,
  /** Разделов с выбранным вариантом из общего числа разделов */
  sectionsSelected: z.number().int(),
  sectionsTotal: z.number().int(),
  /** Разделы без выбранного варианта или с незавершённой себестоимостью (БМ-06) */
  incomplete: z.array(z.object({ sectionLineId: z.uuid(), name: z.string(), missing: z.array(z.string()) })),
  byCostType: z.array(
    z.object({ type: z.string(), title: z.string(), article: z.string().nullable(), amount: Money }),
  ),
  /**
   * Контрактная маржа (БМ-08): выручка по подписанным доходным договорам проекта (без НДС; если доходного
   * договора нет — по смете) минус подписанные расходные договоры по разделам (без НДС) плюс плановая
   * себестоимость разделов без договора и затраты площадки
   */
  contract: z.object({
    /** Подписанных доходных договоров и их сумма без НДС; null — договора нет, выручка взята по смете */
    revenue: Money,
    revenueContracts: z.number().int(),
    /** Подписанные расходные договоры, привязанные позициями к разделам бюджета, без НДС */
    contracted: Money,
    contractsCount: z.number().int(),
    /** Разделов, закрытых договором «под ключ» или по статьям; частично — договор на строку прежней версии бюджета или на статью при выбранном варианте (план раздела остаётся) */
    sectionsCovered: z.number().int(),
    sectionsPartial: z.number().int(),
    /** Разделы без договора — по плановой себестоимости */
    uncoveredCost: Money,
    cost: Money,
    margin: Money,
    marginPct: z.number().nullable(),
  }),
  /**
   * Прогнозная маржа: контрактная (или плановая, пока договора нет) с поправкой на запросы на изменение
   * (6.11), которые ещё не внесены в сметы и бюджет: черновики, на согласовании и утверждённые, но не внедрённые
   */
  forecast: z.object({
    base: z.enum(['contract', 'planned']),
    /** Запросы на согласовании и утверждённые, но не отмеченные внедрёнными; черновики — отдельно, в прогноз не входят */
    changesCount: z.number().int(),
    draftCount: z.number().int(),
    revenueDelta: Money,
    costDelta: Money,
    revenue: Money,
    cost: Money,
    margin: Money,
    marginPct: z.number().nullable(),
    /** Отклонения по причинам изменений: причина → сколько запросов и на сколько меняется выручка и себестоимость */
    byReason: z.array(
      z.object({ reason: z.string(), count: z.number().int(), revenueDelta: Money, costDelta: Money }),
    ),
  }),
  /** Фактическая маржа по оплатам (кассовая, без НДС по ставке на дату платежа): поступления от заказчика минус оплаты */
  actual: z.object({
    received: Money,
    paid: Money,
    margin: Money,
    /** Доля полученного от выручки по договору (или смете), % */
    receivedPct: z.number().nullable(),
    /**
     * Списано со склада на проект (БМ-08): проведённые списания и расход у получателя по цене документа
     * или средней цене прихода; без списаний из принятого отчёта смены (они в `accrued`).
     */
    consumed: Money,
    /** Строк списания без цены: сумма списанного занижена на них */
    consumedUnpriced: z.number().int(),
    /**
     * Начислено по принятым отчётам площадки (БМ-08 / БМ-23): утверждённые оценки
     * `est.site_cost_entries` (люди и техника) плюс материалы из списаний отчёта смены
     * (`wh.stock_documents.client_key` вида `site-report:…`). Сторно оценок уменьшает. Не касса.
     */
    accrued: Money,
    /**
     * Перерасход материалов против нормы выше порога компании (СК-22): сумма отклонения
     * (факт − ожидание) × средняя цена прихода по складам проекта. Не входит в кассовую маржу.
     */
    overNorm: Money,
    /** Материалов с перерасходом выше порога */
    overNormLines: z.number().int(),
    /** Из них без цены — в сумму не вошли */
    overNormUnpriced: z.number().int(),
    /**
     * Фактические расходы на ТО и ремонт техники проекта (ТХ-03 → БМ-07): стоимость записей журнала ТО машин
     * проекта (`pl.machine_services`). Не касса и не входит в маржу: оплата исполнителям идёт отдельно.
     */
    machineService: Money,
    /**
     * Использование инструмента на проекте (СК-32): календарные дни на руках по проекту выдачи × ставка за день
     * экземпляра, с начала истории по сегодня. Не касса и не входит в маржу.
     */
    toolUsage: Money,
    /** Дней на руках у экземпляров на проекте (сумма по экземплярам) */
    toolDays: z.number().int(),
    /** Экземпляров на проекте без ставки за день: их дни в `toolUsage` не вошли */
    toolUnitsNoRate: z.number().int(),
    /** Затраты на ремонт инструмента, отнесённые на проект (по последней выдаче до ремонта) */
    toolRepairs: Money,
    /** Инструмент по месяцам (ГГГГ-ММ): использование и ремонты */
    toolByMonth: z.array(z.object({ month: z.string(), usage: Money, repairs: Money })),
  }),
});
export type ProjectMargin = z.infer<typeof ProjectMargin>;

export const MISSING_LABELS: Record<string, string> = {
  materials: 'нет материалов',
  rate: 'нет ставки',
  delivery: 'нет доставки',
  vat: 'не указано, с НДС ли сумма',
  subcontract: 'нет суммы субподряда',
  estimates: 'к разделу не привязаны сметы',
  variant: 'вариант исполнения не выбран',
  offers: 'нет плановых цен, КП и поставок по материалам',
};

/* ─────────── Затраты площадки и АУП (БМ-07) ─────────── */

/** Виды затрат площадки и статьи ФП (лист «Inputs», БМ-14) */
export const SITE_COST_KINDS = {
  itr_wages: { title: 'ФОТ ИТР', article: null },
  security: {
    title: 'Охрана',
    article: 'Производственные расходы (Ж/д, авиа., корочки. хранение обор-я, охрана)',
  },
  cleaning: { title: 'Клининг', article: 'Административные расходы по проекту' },
  water: { title: 'Вода', article: 'Административные расходы по проекту' },
  fuel: { title: 'ГСМ', article: 'Транспортные расходы (аренда авто, доставка)' },
  machine_service: { title: 'ТО техники', article: 'Транспортные расходы (аренда авто, доставка)' },
  food: {
    title: 'Питание',
    article: 'Производственные расходы (Ж/д, авиа., корочки. хранение обор-я, охрана)',
  },
  leasing: { title: 'Лизинг', article: 'Транспортные расходы (аренда авто, доставка)' },
  rent: { title: 'Аренда', article: 'Административные расходы по проекту' },
  other: { title: 'Прочие расходы площадки', article: 'Административные расходы по проекту' },
} as const;
export type SiteCostKind = keyof typeof SITE_COST_KINDS;
export const SiteCostKindSchema = z.enum(Object.keys(SITE_COST_KINDS) as [SiteCostKind, ...SiteCostKind[]]);

export const SITE_ALLOCATIONS = {
  by_estimate: 'пропорционально смете разделов',
  equal: 'поровну между разделами',
  manual: 'доли вручную',
} as const;
export type SiteAllocation = keyof typeof SITE_ALLOCATIONS;

const Month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Месяц в виде ГГГГ-ММ');

/**
 * Расчёт персонала как в листе «ФОТ» формы (БМ-19): численность, оклад на человека в месяц, коэффициент трудового
 * участия (для ИТР — доля времени на проекте), налоги и отчисления работодателя в процентах от ФОТ.
 * Ставки налогов не зашиты в код: их задаёт компания в строке.
 */
export const STAFF_ROLES = { itr: 'ИТР', installer: 'Монтажники' } as const;
export type StaffRole = keyof typeof STAFF_ROLES;
export const StaffCalc = z.object({
  role: z.enum(Object.keys(STAFF_ROLES) as [StaffRole, ...StaffRole[]]),
  headcount: z.number().int().min(1).max(10000),
  /** Оклад (ставка) одного человека в месяц, ₸ */
  rate: z.number().min(0).max(1e9).finite(),
  /** Коэффициент трудового участия, % (100 — полная занятость на проекте) */
  participation: z.number().min(0).max(100).finite(),
  /** Налоги и отчисления с ФОТ, % */
  taxRate: z.number().min(0).max(100).finite(),
});
export type StaffCalc = z.infer<typeof StaffCalc>;

/** ФОТ с налогами за месяц по строке персонала, ₸ (до копеек) */
export function staffMonthly(s: Pick<StaffCalc, 'headcount' | 'rate' | 'participation' | 'taxRate'>): number {
  return Math.round(s.headcount * s.rate * (s.participation / 100) * (1 + s.taxRate / 100) * 100) / 100;
}

export const SiteCostInput = z.object({
  kind: SiteCostKindSchema,
  name: z.string().trim().min(2).max(200),
  /** До 1 трлн ₸ в месяц: иначе итог за период не влезает в numeric(18,2) и база отвечает ошибкой */
  monthly: z.number().min(0).max(1e12).finite(),
  fromMonth: Month,
  toMonth: Month,
  allocation: z.enum(Object.keys(SITE_ALLOCATIONS) as [SiteAllocation, ...SiteAllocation[]]).optional(),
  /** Доли вручную: раздел бюджета → процент (в сумме 100) */
  shares: z.record(z.uuid(), z.number().min(0).max(100)).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  /** Расчёт ФОТ по листу «ФОТ» (БМ-19): вид становится «ФОТ ИТР», сумма в месяц считается из численности и оклада */
  staff: StaffCalc.nullable().optional(),
});
export type SiteCostInput = z.infer<typeof SiteCostInput>;

export const SiteCost = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  kind: SiteCostKindSchema,
  title: z.string(),
  article: z.string().nullable(),
  name: z.string(),
  monthly: Money,
  fromMonth: z.string(),
  toMonth: z.string(),
  months: z.number().int(),
  total: Money,
  allocation: z.enum(Object.keys(SITE_ALLOCATIONS) as [SiteAllocation, ...SiteAllocation[]]),
  shares: z.record(z.string(), z.number()),
  note: z.string().nullable(),
  staff: StaffCalc.nullable(),
});
export type SiteCost = z.infer<typeof SiteCost>;

/** Затраты площадки версии бюджета: строки, итог, распределение по разделам и месяцам */
export const SiteCostsView = z.object({
  versionId: z.uuid().nullable(),
  canEdit: z.boolean(),
  items: z.array(SiteCost),
  total: Money,
  months: z.array(z.string()),
  /** По разделам: сумма и по месяцам */
  bySection: z.array(
    z.object({
      sectionLineId: z.uuid(),
      name: z.string(),
      estimateAmount: Money,
      total: Money,
      byMonth: z.record(z.string(), Money),
    }),
  ),
  byMonth: z.record(z.string(), Money),
  byArticle: z.array(z.object({ article: z.string().nullable(), amount: Money })),
});
export type SiteCostsView = z.infer<typeof SiteCostsView>;

/** Источник плановой цены проекта (БМ-11) */
export const PRICE_SOURCES = {
  manual: 'вручную',
  offer: 'КП поставщика',
  receipt: 'поступление на склад',
  estimate: 'из сметы',
} as const;
export type PriceSource = keyof typeof PRICE_SOURCES;
export const PriceSourceSchema = z.enum(Object.keys(PRICE_SOURCES) as [PriceSource, ...PriceSource[]]);

/** Строка справочника плановых цен проекта: цена единицы без НДС, откуда взята */
export const ProjectPrice = z.object({
  id: z.uuid(),
  name: z.string(),
  unit: z.string(),
  priceNet: Money,
  source: PriceSourceSchema,
  sourceNote: z.string().nullable(),
  offerId: z.uuid().nullable(),
  documentId: z.uuid().nullable(),
  catalogItemId: z.uuid().nullable(),
  note: z.string().nullable(),
  updatedAt: z.string(),
  /** Цена единицы этого материала по сметам проекта (средняя по количеству), если материал в сметах есть */
  estimatePrice: Money,
  /** Количество по сметам проекта и в каких сметах встречается */
  estimateQuantity: z.number().nullable(),
});
export type ProjectPrice = z.infer<typeof ProjectPrice>;

export const ProjectPriceInput = z.object({
  name: z.string().trim().min(2).max(300),
  unit: z.string().trim().min(1).max(40),
  priceNet: z.number().positive().max(1e12),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type ProjectPriceInput = z.infer<typeof ProjectPriceInput>;

export const ProjectPricesView = z.object({
  items: z.array(ProjectPrice),
  /** Материалы смет проекта без плановой цены — что ещё можно заполнить */
  missing: z.array(
    z.object({ name: z.string(), unit: z.string(), quantity: z.number(), estimatePrice: Money }),
  ),
  canEdit: z.boolean(),
});
export type ProjectPricesView = z.infer<typeof ProjectPricesView>;

/** Заполнение справочника: from = offers — лучшие цены КП и поставок, estimate — цены смет проекта */
export const ProjectPricesFillInput = z.object({
  from: z.enum(['offers', 'estimate']),
  /** Перезаписать строки, заполненные раньше тем же способом (ручные не трогаются) */
  overwrite: z.boolean().optional(),
});
export type ProjectPricesFillInput = z.infer<typeof ProjectPricesFillInput>;

export const ProjectPricesFillResult = z.object({
  added: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
});
export type ProjectPricesFillResult = z.infer<typeof ProjectPricesFillResult>;

/* ─────────── Изменения утверждённого бюджета (БМ-10) ─────────── */

export const BUDGET_CHANGE_KINDS = {
  extra_work: 'допработы',
  saving: 'экономия',
  overrun: 'перерасход',
  transfer: 'перенос между разделами',
  other: 'другое',
} as const;
export type BudgetChangeKind = keyof typeof BUDGET_CHANGE_KINDS;
export const BudgetChangeKindSchema = z.enum(
  Object.keys(BUDGET_CHANGE_KINDS) as [BudgetChangeKind, ...BudgetChangeKind[]],
);

/** Документ-основание: хотя бы одна ссылка (запрос на изменение, допсоглашение, договор или файл) */
export const BudgetChangeInput = z
  .object({
    sectionLineId: z.uuid(),
    kind: BudgetChangeKindSchema,
    delta: z
      .number()
      .finite()
      .min(-1e12)
      .max(1e12)
      .refine((v) => v !== 0, 'Изменение не может быть нулевым'),
    reason: z.string().trim().min(5).max(2000),
    changeId: z.uuid().nullable().optional(),
    amendmentId: z.uuid().nullable().optional(),
    contractId: z.uuid().nullable().optional(),
    fileId: z.uuid().nullable().optional(),
    basisNote: z.string().trim().max(300).nullable().optional(),
  })
  .refine((v) => Boolean(v.changeId || v.amendmentId || v.contractId || v.fileId), {
    message: 'Нужен документ-основание: запрос на изменение, допсоглашение, договор или файл',
    path: ['changeId'],
  })
  .refine((v) => v.kind !== 'saving' || v.delta < 0, {
    message: 'Экономия уменьшает раздел: сумма должна быть отрицательной',
    path: ['delta'],
  })
  .refine((v) => !['extra_work', 'overrun'].includes(v.kind) || v.delta > 0, {
    message: 'Допработы и перерасход увеличивают раздел: сумма должна быть положительной',
    path: ['delta'],
  })
  .refine((v) => !v.fileId || Boolean(v.basisNote?.trim()), {
    message: 'Для файла укажите номер и дату документа',
    path: ['basisNote'],
  });
export type BudgetChangeInput = z.infer<typeof BudgetChangeInput>;

export const BudgetChangeDecision = z.object({
  approve: z.boolean(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type BudgetChangeDecision = z.infer<typeof BudgetChangeDecision>;

export const BudgetChange = z.object({
  id: z.uuid(),
  sectionLineId: z.uuid(),
  sectionName: z.string(),
  kind: BudgetChangeKindSchema,
  delta: Money,
  reason: z.string(),
  status: z.enum(['proposed', 'approved', 'rejected']),
  /** Основание: что за документ и куда ведёт ссылка */
  basis: z.array(
    z.object({
      type: z.enum(['change', 'amendment', 'contract', 'file']),
      id: z.uuid(),
      title: z.string(),
      path: z.string().nullable(),
    }),
  ),
  basisNote: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
});
export type BudgetChange = z.infer<typeof BudgetChange>;

/** Документы проекта, которые могут быть основанием: запросы на изменение и подписанные допсоглашения, договоры */
export const BudgetChangeBasisOptions = z.object({
  changes: z.array(z.object({ id: z.uuid(), title: z.string(), status: z.string(), costDelta: Money })),
  amendments: z.array(z.object({ id: z.uuid(), title: z.string(), contractId: z.uuid(), delta: Money })),
  contracts: z.array(z.object({ id: z.uuid(), title: z.string(), amount: Money })),
});
export type BudgetChangeBasisOptions = z.infer<typeof BudgetChangeBasisOptions>;

export const BudgetChangesView = z.object({
  versionId: z.uuid().nullable(),
  /** Изменения возможны только в утверждённой версии: черновик правится напрямую */
  versionApproved: z.boolean(),
  items: z.array(BudgetChange),
  /** Итог утверждённых изменений и по разделам */
  approvedTotal: Money,
  bySection: z.record(z.string(), Money),
  canPropose: z.boolean(),
  canDecide: z.boolean(),
});
export type BudgetChangesView = z.infer<typeof BudgetChangesView>;

/**
 * Стоимость ресурсов графика работ по неделям рядом с бюджет-графиком оплат (РС-12, 6.6): ФОТ, техника,
 * материалы по назначениям ресурсов работам графика против плана оплат разделов бюджета.
 */
export const ResourceBudgetView = z.object({
  versionId: z.uuid().nullable(),
  /** Понедельники недель, где есть план оплат или стоимость ресурсов */
  weeks: z.array(z.string()),
  /** План оплат бюджета (сумма по разделам текущей версии) по неделям */
  plan: z.array(Money),
  labor: z.array(Money),
  equipment: z.array(Money),
  material: z.array(Money),
  /** Стоимость ресурсов за неделю всего */
  resources: z.array(Money),
  /** Ресурсы − план оплат за неделю (плюс — ресурсы дороже плана) */
  diff: z.array(Money),
  totals: z.object({
    plan: Money,
    labor: Money,
    equipment: Money,
    material: Money,
    resources: Money,
    diff: Money,
  }),
  /** Ресурсы без ставки на дату: их стоимость не учтена */
  missingRates: z.array(z.string()),
});
export type ResourceBudgetView = z.infer<typeof ResourceBudgetView>;

/**
 * Бюджет-график оплат по неделям (БМ-12, 6.6): условия договоров («аванс 30 %», платежи по актам) сами
 * формируют поток платежей проекта; рядом — план оплат разделов бюджета (себестоимость по неделям).
 */
export const PaymentBudgetView = z.object({
  versionId: z.uuid().nullable(),
  /** Понедельники недель */
  weeks: z.array(z.string()),
  /** План оплат разделов бюджета по неделям */
  plan: z.array(Money),
  /** Поступления от заказчика по условиям доходных договоров (ещё не оплаченные) */
  income: z.array(Money),
  /** Платежи по условиям расходных договоров (ещё не оплаченные) */
  expense: z.array(Money),
  /** Поступления − платежи за неделю */
  net: z.array(Money),
  /** Накопленный итог */
  cumulative: z.array(Money),
  totals: z.object({ plan: Money, income: Money, expense: Money, net: Money }),
  /** Платежи по договорам без даты (дата зависит от акта, поставки или счёта) и вне показанных недель */
  undated: z.object({ income: Money, expense: Money }),
  outside: z.object({ income: Money, expense: Money }),
});
export type PaymentBudgetView = z.infer<typeof PaymentBudgetView>;

/**
 * Сверка бюджета с файлом Excel до тенге (ПМ-01, приложение Б ТЗ). Портал не берёт итоги файла на веру: недельные
 * итоги считаются по строкам. Где итог файла расходится с суммой строк — это исправление файла, оно записывается
 * в журнал «было → стало» с адресом ячейки (например, O48 — строка, которую итог недели не учёл).
 */
export const BudgetCorrection = z.object({
  /** Ячейка файла, которую не учёл итог (столбец недели + строка раздела); null — причина не найдена */
  cell: z.string().nullable(),
  /** Понедельник недели; null — общий итог файла */
  periodStart: z.string().nullable(),
  periodLabel: z.string(),
  lineName: z.string().nullable(),
  /** Итог в файле */
  was: Money,
  /** Итог по строкам — так его считает портал */
  became: Money,
  delta: Money,
  reason: z.string(),
});
export type BudgetCorrection = z.infer<typeof BudgetCorrection>;

export const BudgetReconcileLine = z.object({
  row: z.number().int().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  kind: z.enum(['section', 'item', 'alternative']),
  /** Сумма строки в файле; null — строки нет в файле (добавлена в портале) */
  fileAmount: Money,
  /** Сумма строки в портале; null — строки нет в версии бюджета */
  portalAmount: Money,
  /** Сумма по неделям: в файле и в портале */
  fileDistributed: Money,
  portalDistributed: Money,
  /** Недели, где сумма строки в файле и в портале расходится (понедельники) */
  weeks: z.array(z.string()),
  /** Строка есть только в портале (например, вариант исполнения, добавленный после загрузки) */
  addedInPortal: z.boolean(),
});
export type BudgetReconcileLine = z.infer<typeof BudgetReconcileLine>;

export const BudgetReconcileWeek = z.object({
  start: z.string(),
  label: z.string(),
  /** Итог недели в строке «ИТОГО» файла */
  fileTotal: Money,
  /** Итог недели по строкам файла */
  fileLines: Money,
  /** Итог недели в портале (по разделам версии) */
  portal: Money,
  /** ok — всё сходится; corrected — итог файла исправлен по строкам; differs — портал расходится со строками файла */
  status: z.enum(['ok', 'corrected', 'differs']),
});
export type BudgetReconcileWeek = z.infer<typeof BudgetReconcileWeek>;

export const BudgetReconciliation = z.object({
  versionId: z.uuid(),
  versionName: z.string(),
  fileName: z.string(),
  sheet: z.string(),
  /** Строки файла и версии совпадают до тенге, недельные итоги портала равны сумме строк файла */
  matches: z.boolean(),
  totals: z.object({
    /** Итог в файле (контрольная ячейка строки «ИТОГО») */
    file: Money,
    /** Сумма разделов файла */
    fileLines: Money,
    /** Сумма разделов версии в портале, как загружены: без выбранных вариантов исполнения и изменений бюджета */
    portal: Money,
    /** Разложено по неделям: в файле по строкам и в портале */
    fileDistributed: Money,
    portalDistributed: Money,
  }),
  linesChecked: z.number().int(),
  linesMatched: z.number().int(),
  /** Строки, которые расходятся, нет в файле или нет в портале */
  lines: z.array(BudgetReconcileLine),
  weeks: z.array(BudgetReconcileWeek),
  /** Исправления итогов файла: было (файл) → стало (по строкам) */
  corrections: z.array(BudgetCorrection),
});
export type BudgetReconciliation = z.infer<typeof BudgetReconciliation>;

/** Журнал исправлений, записанный при загрузке версии бюджета из файла */
export const BudgetCorrectionsView = z.object({
  versionId: z.uuid(),
  fileName: z.string().nullable(),
  /** null — журнал при загрузке этой версии не вёлся (загружена раньше, чем появился журнал) */
  corrections: z.array(BudgetCorrection).nullable(),
});
export type BudgetCorrectionsView = z.infer<typeof BudgetCorrectionsView>;

/**
 * Сравнение двух версий бюджета проекта (БМ-01): разделы, статьи и варианты исполнения сопоставляются по разделу,
 * виду, номеру и названию; показываются суммы «было → стало», добавленные и убранные строки, смена выбранного
 * варианта и недели, где план оплат изменился. Разница меньше тенге — не изменение.
 */
export const BudgetCompareVersion = z.object({
  id: z.uuid(),
  number: z.number().int(),
  name: z.string(),
  status: z.enum(['draft', 'approved', 'superseded']),
});
export const BudgetCompareLine = z.object({
  kind: z.enum(['section', 'item', 'alternative']),
  code: z.string().nullable(),
  name: z.string(),
  /** Раздел, к которому относится статья или вариант */
  parentName: z.string().nullable(),
  /** null — строки нет в этой версии */
  was: Money,
  became: Money,
  delta: Money,
  /**
   * Раздел: сумма в бюджете — с выбранным вариантом исполнения и утверждёнными изменениями (БМ-10), как в итоге
   * версии на экране; null — не раздел или раздела нет в версии
   */
  effectiveWas: Money,
  effectiveBecame: Money,
  /** Вариант исполнения: выбран ли в версии (null — строки нет) */
  selectedWas: z.boolean().nullable(),
  selectedBecame: z.boolean().nullable(),
  /** Недели, где сумма строки по графику оплат изменилась */
  weeks: z.array(z.string()),
});
export type BudgetCompareLine = z.infer<typeof BudgetCompareLine>;
export const BudgetCompareView = z.object({
  from: BudgetCompareVersion,
  to: BudgetCompareVersion,
  totals: z.object({
    /** Бюджет версии: разделы с выбранными вариантами и утверждёнными изменениями — было и стало */
    was: Money,
    became: Money,
    delta: Money,
    distributedWas: Money,
    distributedBecame: Money,
  }),
  linesSame: z.number().int(),
  lines: z.array(BudgetCompareLine),
  weeks: z.array(z.object({ start: z.string(), was: Money, became: Money, delta: Money })),
});
export type BudgetCompareView = z.infer<typeof BudgetCompareView>;

/**
 * Себестоимость раздела бюджета до позиции сметы (БМ-01): плановая себестоимость выбранного варианта исполнения
 * раскладывается по позициям привязанных смет — материалы по материалам позиций, оборудование по оборудованию, работы
 * (ФОТ, начисления, вахта, суточные, механизмы, субподряд) по трудозатратам и механизмам, накладные и непредвиденные —
 * пропорционально прямым затратам позиции. Варианта нет — по сметной стоимости позиций от суммы раздела.
 */
export const SectionItemCostRow = z.object({
  itemId: z.uuid(),
  estimateId: z.uuid(),
  estimateCode: z.string(),
  number: z.number().int(),
  code: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  /** Сметная стоимость позиции */
  estimateAmount: Money,
  /** Плановая себестоимость: по видам затрат (null — вариант не выбран, расклад только итогом) */
  materials: Money,
  equipment: Money,
  works: Money,
  overheads: Money,
  /** Доля утверждённых изменений бюджета раздела (БМ-10) — по сметной стоимости позиций */
  changes: Money,
  planned: Money,
  /** Сметная стоимость − плановая себестоимость */
  margin: Money,
});
export type SectionItemCostRow = z.infer<typeof SectionItemCostRow>;
export const SectionItemCost = z.object({
  sectionLineId: z.uuid(),
  /**
   * variant — по расчёту выбранного варианта; selected — выбран вариант без расчёта (из Excel), итогом; section —
   * вариант не выбран, по сумме раздела; none — позиций нет
   */
  source: z.enum(['variant', 'selected', 'section', 'none']),
  variantName: z.string().nullable(),
  /** План раздела: вариант или сумма раздела плюс утверждённые изменения бюджета */
  plannedTotal: Money,
  changesTotal: Money,
  /** Сметы раздела изменились после расчёта варианта — расчёт стоит обновить */
  stale: z.boolean(),
  estimateTotal: Money,
  itemsTotal: z.number().int(),
  /** Показаны не все позиции: самые крупные по плану, от большего к меньшему */
  truncated: z.boolean(),
  items: z.array(SectionItemCostRow),
});
export type SectionItemCost = z.infer<typeof SectionItemCost>;
